import React, { useState, useEffect } from "react";
import styled from "styled-components";
import SidebarMenu from "./SidebarMenu";
import menuConfig from "../../../config/menuConfig";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getLogoByCustomerId } from "../../../services/settingsSerice";
import { FaBars, FaTimes } from "react-icons/fa";
import customerService from "../../../services/customerService";

const Sidebar = () => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [altText, setAltText] = useState("");
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar toggle state
  const [isVoucherCustomer, setIsVoucherCustomer] = useState(false);

  const toggleMenu = (title) => {
    setActiveMenu((prevActiveMenu) => (prevActiveMenu === title ? null : title));
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/auth/login");
  };

  const handleProfileClick = () => {
    navigate("/auth/profile");
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const data = await getLogoByCustomerId(token);
        if (data) {
          setLogoUrl(data.logoImageUrl || "");
          setAltText(data.alterativeText || "Company Logo");
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }

      // Fetch customer's voucher flag when available
      try {
        const customerId = user?.customerId;
        if (customerId) {
          const customer = await customerService.getCustomerById(customerId);
          setIsVoucherCustomer(!!customer?.isVoucherCustomer);
        } else {
          setIsVoucherCustomer(false);
        }
      } catch (err) {
        console.error("Failed to fetch customer details:", err);
        setIsVoucherCustomer(false);
      }
    };
    fetchData();
  }, [token, user?.customerId]);

  return (
      <>
        {/* 🍔 Hamburger Button (Mobile Only) */}
        <HamburgerButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </HamburgerButton>

        {/* Sidebar */}
        <SidebarContainer $isOpen={isSidebarOpen}>
          <LogoContainer>
            <LogoImage src={logoUrl} alt={altText} />
          </LogoContainer>
          <MenuWrapper>
            <SidebarMenu menuConfig={menuConfig} toggleMenu={toggleMenu} activeMenu={activeMenu} closeSidebar={() => setIsSidebarOpen(false)} isVoucherCustomer={isVoucherCustomer}/>
          </MenuWrapper>
          <UserProfileSection>
            <UserInfo onClick={handleProfileClick}>
              <UserDetails>
                <UserName>{user?.firstName} {user?.lastName}</UserName>
                <UserEmail>{user?.normalizedEmail}</UserEmail>
              </UserDetails>
            </UserInfo>
            <UserOption onClick={handleProfileClick}>Mitt konto</UserOption>
            <LogoutOption onClick={handleLogout}>Logga ut</LogoutOption>
          </UserProfileSection>
        </SidebarContainer>

        {/* Background Overlay for Mobile */}
        {isSidebarOpen && <Overlay onClick={() => setIsSidebarOpen(false)} />}
      </>
  );
};

export default Sidebar;


const SidebarContainer = styled.div`
    width: 250px;
    background: #ffffff;
    position: fixed;
    left: 0;
    top: 60px;
    height: calc(100vh - 60px);  
    z-index: 1000;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow-y: auto;
    padding: 0;
    transition: transform 0.3s ease-in-out;
    transform: ${(props) => (props.$isOpen ? "translateX(0)" : "translateX(-100%)")};

    @media (min-width: 768px) {
        transform: translateX(0); 
        position: fixed; 
        height: calc(100vh - 60px); 
        top: 60px;
    }
`;



const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
`;

const LogoImage = styled.img`
  height: 100%;
  padding:20px 60px 0px 60px
`;

const MenuWrapper = styled.div`
  padding: 20px;
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0; 
`;

const UserProfileSection = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 10;
    background-color: #fff;
    padding: 20px;
    border-top: 1px solid #e0e0e0;
    @media (max-width: 480px) {
        padding: 12px 12px;
    }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 15px;
  //padding: 10px;
  border-radius: 8px;
  transition: background-color 0.3s, box-shadow 0.3s;

  &:hover {
    background-color: #f9f9f9;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
`;

const UserAvatar = styled.img`
  border-radius: 50%;
  width: 60px;
  height: 60px;
  margin-right: 15px;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* Align items to the left */
  gap: 2px; /* Reduce the gap between the username and email */
  text-align: left; /* Ensure text is left-aligned */
`;

const UserName = styled.div`
  font-weight: bold;
  color: #333333;
  font-size: 1rem;
  margin: 0;
  @media (max-width: 767px) {
    font-size: 1.17rem;
  }
`;

const UserEmail = styled.div`
    font-size: 0.875rem;
    color: #777777;
    margin: 0;
    @media (max-width: 767px) {
        font-size: 1.02rem;
    }
`;

const UserOption = styled.div`
    font-size: 0.9rem;
    color: #000;
    font-weight: 500;
    cursor: pointer;
    margin-bottom: 8px;
    text-align: left;
    width: 100%;
    transition: color 0.3s;
    @media (max-width: 767px) {
        font-size: 1.05rem;
    }
`;

const LogoutOption = styled.div`
    font-size: 0.9rem;
    color: #000;
    font-weight: bold;
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: color 0.3s;
    @media (max-width: 767px) {
        font-size: 1.3rem;
    }
`;

const HamburgerButton = styled.button`
  position: fixed;
  top: 15px;
  left: 15px;
  z-index: 1100;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  @media (min-width: 768px) {
    display: none; /* Hide button on desktop */
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  display: ${(props) => (props.isOpen ? "block" : "none")};

  @media (min-width: 768px) {
    display: none;
  }
`;